import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Camera, Check, RefreshCw, Search, ExternalLink, Pencil, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { nyDateKey, nyToday } from '../../lib/datetime';
import MediaLinkDialog from '../../components/admin/MediaLinkDialog';

// /admin/shoots — dedicated media-operations page (CJ's home base).
//
// Filter chips: All / Scheduled / Overdue / TBD / Completed
//   open-status filters (All/Scheduled/Overdue/TBD) hit the endpoint with
//   ?status=open. The Completed chip switches to ?status=completed and the
//   row layout flips: instead of "Mark complete" the action becomes a
//   "View media ↗" link to the stored media library URL.
//
// Action: "Mark complete" opens MarkCompleteDialog which prompts for an
// optional Media library URL (Drive, Dropbox, etc). The URL is persisted
// onto the deliverable's asset_urls_json so the Project page tile + this
// page's Completed view both surface it.

const FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'overdue',   label: 'Overdue' },
  { key: 'tbd',       label: 'TBD' },
  { key: 'completed', label: 'Completed' },
];

export default function Shoots() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'all';
  const q = (searchParams.get('q') || '').trim();
  const [searchInput, setSearchInput] = useState(q);

  const [shoots, setShoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [linkDialog, setLinkDialog] = useState(null); // { shoot, mode: 'complete' | 'edit' }

  // The Completed chip needs a different server-side query (status=completed).
  // All other filters operate on the open set client-side.
  const fetchStatus = filter === 'completed' ? 'completed' : 'open';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/projects/upcoming-shoots?status=${fetchStatus}`);
      setShoots(res?.shoots || []);
    } catch (err) {
      setError(err?.message || 'Failed to load shoots');
    } finally {
      setLoading(false);
    }
  }, [fetchStatus]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'all') next.delete('filter'); else next.set('filter', key);
    setSearchParams(next, { replace: true });
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams(searchParams);
    const v = searchInput.trim();
    if (!v) next.delete('q'); else next.set('q', v);
    setSearchParams(next, { replace: true });
  };

  const today = nyToday();

  // counts for chip badges. Only meaningful when viewing the open set —
  // when filter=completed we skip the open-tier counts.
  const counts = useMemo(() => {
    if (filter === 'completed') {
      return { all: 0, scheduled: 0, overdue: 0, tbd: 0, completed: shoots.length };
    }
    let scheduled = 0, overdue = 0, tbd = 0;
    for (const s of shoots) {
      if (s.tbd) tbd += 1;
      else if (s.shootDate && s.shootDate < today) overdue += 1;
      else scheduled += 1;
    }
    return { all: shoots.length, scheduled, overdue, tbd, completed: null };
  }, [shoots, today, filter]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return shoots.filter((s) => {
      if (filter === 'tbd' && !s.tbd) return false;
      if (filter === 'overdue' && (s.tbd || !(s.shootDate && s.shootDate < today))) return false;
      if (filter === 'scheduled' && (s.tbd || (s.shootDate && s.shootDate < today))) return false;
      // 'all' and 'completed' have no client-side filter beyond search.
      if (ql) {
        const hay = `${s.accountName || ''} ${s.projectName || ''} ${s.schoolContact || ''}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [shoots, filter, q, today]);

  // Mark-complete writes the URL via the /complete endpoint (also flips
  // status=delivered + sets completed_date). Once a shoot is already
  // delivered, we PATCH the deliverable directly to avoid resetting
  // completed_date — pure metadata edit.
  const submitLink = async ({ deliverableId, mediaUrl, mode }) => {
    if (!deliverableId) return;
    if (mode === 'complete') {
      const body = mediaUrl ? { assetUrls: [mediaUrl] } : null;
      await api.post(`/api/admin/deliverables/${deliverableId}/complete`, body);
      setShoots((prev) => prev.filter((x) => x.deliverableId !== deliverableId));
    } else {
      // edit on a delivered shoot — pass null to clear, otherwise stringify.
      const assetUrlsJson = mediaUrl ? JSON.stringify([mediaUrl]) : null;
      const res = await api.patch(`/api/admin/deliverables/${deliverableId}`, { assetUrlsJson });
      const updated = res?.deliverable;
      setShoots((prev) => prev.map((x) => {
        if (x.deliverableId !== deliverableId) return x;
        // Re-parse asset_urls_json to refresh mediaUrl/mediaUrls on the row.
        let urls = [];
        if (updated?.assetUrlsJson) {
          try {
            const parsed = JSON.parse(updated.assetUrlsJson);
            if (Array.isArray(parsed)) urls = parsed.filter((u) => typeof u === 'string' && u);
          } catch { /* fallthrough */ }
        }
        return { ...x, mediaUrls: urls, mediaUrl: urls[0] || null };
      }));
    }
    setLinkDialog(null);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#B0FC38]" />
            <span className="serif-acc accent-text">Shoots</span>
          </h1>
          <p className="text-[12px] text-gray-500 mt-1">
            Active projects whose media capture is still open. Mark a shoot complete (optionally linking the media library) to close the project's media_capture deliverable.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-300 hover:text-white border border-white/[0.08] hover:border-white/[0.15] disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {filter !== 'completed' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Open shoots" value={counts.all} />
          <Stat label="Scheduled" value={counts.scheduled} tone="ok" />
          <Stat label="Overdue" value={counts.overdue} tone={counts.overdue > 0 ? 'alert' : null} />
          <Stat label="TBD" value={counts.tbd} tone={counts.tbd > 0 ? 'warn' : null} />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Completed shoots" value={shoots.length} tone="ok" />
          <Stat
            label="With media link"
            value={shoots.filter((s) => s.mediaUrl).length}
            tone="ok"
          />
          <Stat
            label="No media link"
            value={shoots.filter((s) => !s.mediaUrl).length}
            tone={shoots.filter((s) => !s.mediaUrl).length > 0 ? 'warn' : null}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            const count = counts[f.key];
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`shoots-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                  active
                    ? 'bg-[#B0FC38]/15 border-[#B0FC38]/40 text-white'
                    : 'bg-white/[0.02] border-white/[0.08] text-gray-400 hover:text-white hover:border-white/[0.15]'
                }`}
              >
                {f.label}
                {typeof count === 'number' && (
                  <span className="ml-1.5 text-[10px] text-gray-500 font-mono">{count}</span>
                )}
              </button>
            );
          })}
        </div>
        <form onSubmit={submitSearch} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search account or project…"
            data-testid="shoots-search"
            className="pl-9 pr-3 py-2 rounded-full bg-white/[0.03] border border-white/[0.08] text-[12px] text-white placeholder:text-gray-500 w-[260px] focus:outline-none focus:border-[#B0FC38]/40"
          />
        </form>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {error}
        </div>
      )}

      {loading && shoots.length === 0 ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-white/[0.03] animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-10 text-center">
          <Camera className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <div className="text-[13px] text-gray-400">
            {filter === 'completed'
              ? 'No completed shoots yet. Mark a shoot complete to see it here.'
              : shoots.length === 0
                ? 'No open shoots right now. New media_capture deliverables will appear here automatically.'
                : 'No shoots match the current filter.'}
          </div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="shoots-list">
          {filtered.map((s) => (
            <ShootRow
              key={s.projectId}
              shoot={s}
              today={today}
              onComplete={(shoot) => setLinkDialog({ shoot, mode: 'complete' })}
              onEditLink={(shoot) => setLinkDialog({ shoot, mode: 'edit' })}
              completedView={filter === 'completed'}
            />
          ))}
        </ul>
      )}

      <MediaLinkDialog
        open={!!linkDialog}
        mode={linkDialog?.mode || 'complete'}
        deliverableId={linkDialog?.shoot?.deliverableId}
        existingUrl={linkDialog?.shoot?.mediaUrl || ''}
        headerTitle={linkDialog?.shoot?.accountName || linkDialog?.shoot?.projectName}
        subtitle={
          linkDialog?.shoot
            ? (linkDialog.mode === 'edit'
                ? (linkDialog.shoot.completedAt ? `Completed ${nyDateKey(linkDialog.shoot.completedAt)}` : 'Completed')
                : linkDialog.shoot.tbd
                  ? 'Shoot date TBD'
                  : linkDialog.shoot.shootDate ? `Shot ${linkDialog.shoot.shootDate}` : '')
            : ''
        }
        onClose={() => setLinkDialog(null)}
        onSubmit={submitLink}
      />
    </div>
  );
}

function Stat({ label, value, tone = null }) {
  const toneCls = tone === 'alert' ? 'text-red-300' : tone === 'warn' ? 'text-amber-300' : tone === 'ok' ? 'text-emerald-300' : 'text-white';
  return (
    <div className="p-3 rounded-xl border border-white/[0.05] bg-white/[0.015]">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`text-[22px] font-semibold mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}

function ShootRow({ shoot, today, onComplete, onEditLink, completedView }) {
  const overdue = !completedView && !shoot.tbd && shoot.shootDate && shoot.shootDate < today;
  const dateTone = completedView
    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25'
    : shoot.tbd
      ? 'bg-gray-500/10 text-gray-300 border-gray-500/25'
      : overdue
        ? 'bg-red-500/10 text-red-300 border-red-500/30'
        : 'bg-[#B0FC38]/10 text-[#B0FC38] border-[#B0FC38]/30';
  const dateLabel = completedView
    ? (shoot.completedAt ? nyDateKey(shoot.completedAt) : '—')
    : shoot.tbd ? 'TBD' : shoot.shootDate;
  const dateHeading = completedView
    ? 'Completed'
    : shoot.tbd ? 'Shoot' : overdue ? 'Overdue' : 'Scheduled';

  return (
    <li
      data-testid={`shoot-row-${shoot.projectId}`}
      className="flex items-center gap-4 p-3.5 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.035] transition-colors"
    >
      <div className={`shrink-0 w-28 text-center px-2 py-2 rounded-lg border ${dateTone}`}>
        <div className="text-[10px] uppercase tracking-wider opacity-70">{dateHeading}</div>
        <div className="text-[12.5px] font-mono font-semibold mt-0.5">{dateLabel}</div>
      </div>

      <Link to={`/admin/projects/${shoot.projectId}`} className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] text-white font-medium truncate">{shoot.accountName || shoot.projectName}</span>
          {!completedView && shoot.projectStatus && shoot.projectStatus !== 'media_capture' && (
            <span
              title={`Project is currently in ${shoot.projectStatus.replace(/_/g, ' ')}, media capture is still open`}
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/25 text-amber-300"
            >
              {shoot.projectStatus.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        {shoot.accountName && shoot.projectName && shoot.accountName !== shoot.projectName && (
          <div className="text-[11px] text-gray-500 truncate mt-0.5">{shoot.projectName}</div>
        )}
        {shoot.schoolContact && (
          <div className="text-[11px] text-gray-400 mt-1 truncate">
            <span className="text-gray-600">Contact:</span> {shoot.schoolContact}
          </div>
        )}
        {shoot.mediaSubtypes && shoot.mediaSubtypes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {shoot.mediaSubtypes.map((s) => (
              <span key={s} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.06] text-gray-300">
                {s.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </Link>

      {completedView ? (
        <div className="shrink-0 flex items-center gap-1">
          {shoot.mediaUrl ? (
            <>
              <a
                href={shoot.mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`shoot-view-media-${shoot.projectId}`}
                onClick={(e) => e.stopPropagation()}
                title={shoot.mediaUrl}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3.5 py-2 rounded-full border border-[#B0FC38]/40 bg-[#B0FC38]/10 text-[#B0FC38] hover:bg-[#B0FC38]/20 hover:text-white hover:border-[#B0FC38]/60 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View media
              </a>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEditLink(shoot); }}
                data-testid={`shoot-edit-link-${shoot.projectId}`}
                title="Edit or remove media link"
                className="p-2 rounded-full text-gray-500 hover:text-white hover:bg-white/[0.05] transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEditLink(shoot); }}
              data-testid={`shoot-add-link-${shoot.projectId}`}
              title="Add a media library URL to this completed shoot"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3.5 py-2 rounded-full border border-white/[0.12] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] hover:text-white hover:border-white/[0.2] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add link
            </button>
          )}
        </div>
      ) : (
        shoot.deliverableId && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onComplete(shoot); }}
            data-testid={`shoot-mark-done-${shoot.projectId}`}
            title="Mark this shoot completed and optionally link the media library"
            className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-medium px-3.5 py-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:text-white hover:border-emerald-500/60 transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
            Mark complete
          </button>
        )
      )}
    </li>
  );
}

